import React, { useState, useEffect, useRef } from 'react';
import { Paper, Title, Text, TextInput, Select, Button, Group, Stack, Divider, Radio, Alert } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import MusicUpload from '../MusicUpload/MusicUpload';
import {
  isAdultDivision, categoriesForDivision, COMPETITIVE_CATEGORY, FOOTWEAR_OPTIONS,
  POINTE_EXPECTED_FROM_AGE, suggestedFootwear, softShoeWarning,
} from '../../config/categories';

const inputStyles = { label: { textAlign: 'left' }, description: { textAlign: 'left' } };

const MAX_SOLOS = 2;

const VARIATION_LENGTHS = [
  { value: '1 minute', label: '1 minute' },
  { value: '1.5 minutes', label: '1.5 minutes' },
  { value: '2 minutes', label: '2 minutes' },
  { value: '2.5 minutes', label: '2.5 minutes' },
  { value: '3 minutes', label: '3 minutes' }
];

const SOLO_FIELDS = [
  'performance_category', 'footwear', 'variation_name', 'ballet_origin', 'character_name',
  'variation_length', 'start_music', 'music_file_url', 'music_file_name',
];
const fromRow = (row) => Object.fromEntries(SOLO_FIELDS.map(f => [f, row?.[f] || '']));

// Youth: soft under 12, pointe from 12 (age on Jan 1). Adults choose.
const defaultFootwear = (dateOfBirth, adult) => (adult ? '' : suggestedFootwear(dateOfBirth));

const SoloBlock = ({ number, title, data, onChange, onMusicUpload, onRemove, registrationId, ageDivision, dateOfBirth, errors = {}, readOnly = false }) => {
  const isAdult = isAdultDivision(ageDivision);
  const warning = softShoeWarning({ dateOfBirth, footwear: data.footwear, isAdultEntry: isAdult });

  return (
    <div>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" color="var(--deep-gold)">{title}</Text>
        {onRemove && !readOnly && (
          <Button variant="subtle" color="red" size="xs" leftSection={<IconTrash size={14} />} onClick={onRemove}>
            Remove
          </Button>
        )}
      </Group>
      <Stack gap="sm">
        <Select
          label="Performance Category"
          placeholder={isAdult ? 'Select category' : COMPETITIVE_CATEGORY}
          required
          data={categoriesForDivision(ageDivision)}
          value={isAdult ? data.performance_category : COMPETITIVE_CATEGORY}
          onChange={(v) => isAdult && onChange('performance_category', v)}
          error={errors.performance_category}
          styles={inputStyles}
          disabled={readOnly || !isAdult}
          description={[
            ageDivision && `Age division: ${ageDivision} (from the date of birth).`,
            !isAdult && ageDivision && 'Students under 18 compete in Competitive.',
          ].filter(Boolean).join(' ') || undefined}
        />
        <div>
          <Radio.Group
            label="Footwear"
            description={isAdult
              ? 'Adults choose pointe or soft shoes (Adult Recreational is judged separately for each).'
              : `Filled in from age: soft shoes under ${POINTE_EXPECTED_FROM_AGE}, pointe from ${POINTE_EXPECTED_FROM_AGE}. Change it if this student dances differently.`}
            required
            value={data.footwear || ''}
            onChange={(v) => onChange('footwear', v)}
            error={errors.footwear}
            styles={inputStyles}
          >
            <Group mt="xs" gap="xl">
              {FOOTWEAR_OPTIONS.map(o => <Radio key={o.value} value={o.value} label={o.label} disabled={readOnly} />)}
            </Group>
          </Radio.Group>
          {warning && (
            <Alert color="yellow" variant="light" mt="xs" p="xs">
              <Text size="sm" style={{ textAlign: 'left' }}>{warning}</Text>
            </Alert>
          )}
        </div>
        <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
          The piece details and music can be added later, until registration closes.
        </Text>
        <Group grow>
          <TextInput
            label="Variation Name"
            placeholder="e.g. Kitri Variation"
            value={data.variation_name}
            onChange={(e) => onChange('variation_name', e.target.value)}
            styles={inputStyles}
            disabled={readOnly}
          />
          <TextInput
            label="Ballet / Music Origin"
            placeholder="e.g. Don Quixote"
            value={data.ballet_origin}
            onChange={(e) => onChange('ballet_origin', e.target.value)}
            styles={inputStyles}
            disabled={readOnly}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Character Name"
            placeholder="e.g. Kitri (optional)"
            value={data.character_name}
            onChange={(e) => onChange('character_name', e.target.value)}
            styles={inputStyles}
            disabled={readOnly}
          />
          <Select
            label="Variation Length"
            placeholder="Select length"
            data={VARIATION_LENGTHS}
            value={data.variation_length || null}
            onChange={(v) => onChange('variation_length', v || '')}
            styles={inputStyles}
            disabled={readOnly}
          />
        </Group>
        <Radio.Group
          label="Start Music"
          description="Will the dancer be ON stage or OFF stage when the music begins?"
          value={data.start_music || ''}
          onChange={(v) => onChange('start_music', v)}
          styles={inputStyles}
        >
          <Group mt="xs" gap="xl">
            <Radio value="on_stage" label="ON Stage" disabled={readOnly} />
            <Radio value="off_stage" label="OFF Stage" disabled={readOnly} />
          </Group>
        </Radio.Group>
        <div>
          <Text size="xs" fw={500} mb={4} style={{ textAlign: 'left' }}>Music</Text>
          <MusicUpload
            registrationId={registrationId}
            variationNumber={number}
            existingMusicUrl={data.music_file_url}
            existingMusicFileName={data.music_file_name}
            onUploadComplete={({ url, fileName }) => onMusicUpload && onMusicUpload(url, fileName)}
            onUploadError={() => {}}
          />
        </div>
      </Stack>
    </div>
  );
};

/**
 * A student's solos in the school portal: 0–2, each optional (a student can
 * be in ensembles only). Reports the full list to the parent on every change;
 * the parent saves it, deleting any solo that's no longer listed.
 */
const StudentVariationSection = ({
  registrationId,
  ageDivision,
  dateOfBirth,
  initialVariations, // undefined while the dashboard is still loading them
  onVariationsChange,
  readOnly = false
}) => {
  // [{ number, data }] ordered by slot.
  const [solos, setSolos] = useState([]);
  const isAdult = isAdultDivision(ageDivision);

  // Load the saved solos once per student. After every autosave the dashboard
  // hands back the list it just saved; re-loading from it would overwrite
  // whatever was typed while that save was in flight.
  const loadedForRef = useRef(null);
  useEffect(() => {
    if (initialVariations === undefined || loadedForRef.current === registrationId) return;
    loadedForRef.current = registrationId;
    setSolos(
      initialVariations
        .map(v => ({ number: v.variation_number, data: fromRow(v) }))
        .sort((a, b) => a.number - b.number)
    );
  }, [initialVariations, registrationId]);

  const report = (next) => {
    if (!onVariationsChange) return;
    onVariationsChange(next.map(s => ({ variation_number: s.number, age_division: ageDivision, ...s.data })));
  };

  // Youth students: Competitive only, and fill any empty footwear from age once
  // the birthday is known. Teacher changes are never overwritten.
  // Set the list and tell the parent — never from inside a state updater.
  const commit = (next) => {
    setSolos(next);
    report(next);
  };

  useEffect(() => {
    if (!ageDivision || readOnly) return;
    let changed = false;
    const next = solos.map(s => {
      const data = { ...s.data };
      if (!isAdult && data.performance_category !== COMPETITIVE_CATEGORY) { data.performance_category = COMPETITIVE_CATEGORY; changed = true; }
      if (!data.footwear) {
        const fill = defaultFootwear(dateOfBirth, isAdult);
        if (fill) { data.footwear = fill; changed = true; }
      }
      return { ...s, data };
    });
    if (changed) commit(next);
  }, [ageDivision, dateOfBirth, solos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (number, fields) => {
    commit(solos.map(s => (s.number === number ? { ...s, data: { ...s.data, ...fields } } : s)));
  };

  const addSolo = () => {
    const number = [1, 2].find(n => !solos.some(s => s.number === n));
    if (!number) return;
    const data = {
      ...fromRow(null),
      performance_category: isAdult ? '' : COMPETITIVE_CATEGORY,
      footwear: defaultFootwear(dateOfBirth, isAdult),
    };
    commit([...solos, { number, data }].sort((a, b) => a.number - b.number));
  };

  const removeSolo = (number) => commit(solos.filter(s => s.number !== number));

  return (
    <Paper p="md" radius="md" withBorder style={{ background: '#fafafa' }}>
      <Group justify="space-between" mb={solos.length ? 'md' : 'xs'} wrap="wrap" gap="xs">
        <Group gap="xs" align="baseline">
          <Title order={5} style={{ color: 'var(--charcoal)', margin: 0 }}>Solos</Title>
          <Text size="sm" color="dimmed">{solos.length} of {MAX_SOLOS}</Text>
        </Group>
        {!readOnly && solos.length < MAX_SOLOS && (
          <Button variant="subtle" color="gold" size="sm" leftSection={<IconPlus size={16} />} onClick={addSolo}>
            Add a solo
          </Button>
        )}
      </Group>

      {solos.length === 0 && (
        <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
          No solos. A student can also be entered in ensembles only (next step).
        </Text>
      )}

      {solos.map((s, i) => (
        <React.Fragment key={s.number}>
          {i > 0 && <Divider my="lg" />}
          <SoloBlock
            number={s.number}
            title={solos.length > 1 ? `Solo ${i + 1}` : 'Solo'}
            data={s.data}
            onChange={(field, value) => update(s.number, { [field]: value })}
            onMusicUpload={(url, fileName) => update(s.number, { music_file_url: url, music_file_name: fileName })}
            onRemove={() => removeSolo(s.number)}
            registrationId={registrationId}
            ageDivision={ageDivision}
            dateOfBirth={dateOfBirth}
            readOnly={readOnly}
          />
        </React.Fragment>
      ))}
    </Paper>
  );
};

export default StudentVariationSection;
